// src/features/editor/components/shared/useFileUpload.ts
import { useState } from 'react'
import { toast } from 'sonner'
import { useAppSelector } from '@/store/hooks'
import { selectAuthToken } from '@/features/auth/selectors'
import { uploadFileWithProgress, type CompressionType } from '@/api/uploadFile'
import { useDeleteFileMutation } from '../../uploadsApi'
import { FIELD_LIMITS } from '../../constants/fieldLimits'
import { uploadErrorText } from '@/api/uploadError'
import { preloadImage } from '@/lib/preloadImage'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

interface Options {
  compressionType?: CompressionType
}

/** Параметры ОДНОЙ загрузки: у очереди хук один на редактор, а тип сжатия и
 *  получатель прогресса — свои у каждой задачи. */
interface UploadOptions {
  compressionType?: CompressionType
  /** Прогресс для вызывающего (`null` — загрузка закончилась). Свой `progress`
   *  хука при этом продолжает жить: им пользуются места без очереди. */
  onProgress?: (percent: number | null) => void
}

interface Result {
  progress: number | null
  upload: (file: File, opts?: UploadOptions) => Promise<string | null>
  removeFile: (url: string) => void
}

// Общая логика загрузки файла с прогрессом + best-effort удаление старого блоба.
// Раньше была построчно продублирована в ImageUpload и MascotSlot (включая англоязычные
// тексты тостов) — теперь единственное место, оба компонента переиспользуют её.
export const useFileUpload = ({ compressionType = 'banner_compression' }: Options = {}): Result => {
  const accessToken = useAppSelector(selectAuthToken)
  const [progress, setProgress] = useState<number | null>(null)
  const [deleteFile] = useDeleteFileMutation()

  const upload = async (file: File, opts: UploadOptions = {}): Promise<string | null> => {
    if (file.size > FIELD_LIMITS.UPLOAD_MAX_BYTES) {
      toast.error(messagesNow(messages).fileTooBig(FIELD_LIMITS.UPLOAD_MAX_MB))
      return null
    }
    const report = (percent: number | null) => {
      setProgress(percent)
      opts.onProgress?.(percent)
    }
    report(0)
    try {
      const url = await uploadFileWithProgress({
        file,
        compressionType: opts.compressionType ?? compressionType,
        accessToken,
        onProgress: (p) => report(p.percent),
      })
      // Проценты держим, пока картинка не скачалась, — иначе место секунду пустое.
      if (url && file.type.startsWith('image/')) await preloadImage(url)
      return url
    } catch (e) {
      toast.error(uploadErrorText(e))
      return null
    } finally {
      report(null)
    }
  }

  const removeFile = (url: string) => {
    deleteFile({ fileUrl: url }).catch(() => {}) // silent best-effort
  }

  return { progress, upload, removeFile }
}
